// TO POPULATE: paste recommendations from
// https://www.linkedin.com/in/fppalumbo/details/recommendations/
// Each entry: name, role+company, profile URL, recommendation text.
// Photos: download from LinkedIn, save to dioscuro/static/images/recs/<slug>.jpg
import React, { useRef } from "react";
import styled from "styled-components";
import { Container } from "react-bootstrap";

const Section = styled.section`
  padding: 96px 0;
  background: var(--nd-surface);
`;

const SectionLabel = styled.p`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  margin: 0 0 16px;
`;

const SectionTitle = styled.h2`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 36px;
  font-weight: 500;
  color: var(--nd-text-display);
  letter-spacing: -0.02em;
  line-height: 1.1;
  margin: 0 0 16px;
`;

const SectionIntro = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 16px;
  color: var(--nd-text-secondary);
  line-height: 1.5;
  max-width: 560px;
  margin: 0 0 32px;
`;

const Logos = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 48px;
`;

const Chip = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 13px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--nd-text-primary);
  padding: 12px 20px;
  border: 1px solid var(--nd-border-visible);
  border-radius: 6px;
  background: var(--nd-black);
`;

/* ── Carousel ───────────────────────────────────────────── */

const CarouselWrap = styled.div`
  position: relative;
`;

const Track = styled.div`
  display: flex;
  gap: 16px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding: 4px 0 16px;
  &::-webkit-scrollbar { display: none; }
`;

const RecCard = styled.article`
  flex: 0 0 calc((100% - 16px) / 2.5);
  scroll-snap-align: start;
  background: var(--nd-surface);
  border: 1px solid var(--nd-border-visible);
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  min-height: 280px;

  @media (max-width: 768px) {
    flex: 0 0 100%;
    scroll-snap-align: center;
  }
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const Photo = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${({ src }) => src ? `center/cover no-repeat url(${src})` : "var(--nd-text-disabled)"};
  flex-shrink: 0;
`;

const Who = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const Name = styled.span`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 15px;
  font-weight: 500;
  color: var(--nd-text-display);
  line-height: 1.2;
`;

const Role = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.04em;
  color: var(--nd-text-disabled);
  margin-top: 2px;
`;

const Body = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 14px;
  color: var(--nd-text-primary);
  line-height: 1.6;
  margin: 0 0 16px;
  flex-grow: 1;
`;

const ProfileLink = styled.a`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-disabled) !important;
  text-decoration: none !important;
  align-self: flex-start;
  &:hover { color: var(--nd-text-primary) !important; }
  &::after { content: ' \\2197'; }
`;

const ArrowBtn = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${({ side }) => side === "left" ? "left: -16px;" : "right: -16px;"}
  width: 32px;
  height: 32px;
  background: var(--nd-surface);
  border: 1px solid var(--nd-border-visible);
  border-radius: 4px;
  color: var(--nd-text-secondary);
  font-family: 'Space Mono', monospace;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  outline: none;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  &:hover {
    color: var(--nd-text-display);
    border-color: var(--nd-text-display);
  }
  @media (max-width: 768px) {
    display: none;
  }
`;

/* ── Data ───────────────────────────────────────────────── */

const CLIENTS = ["Endava", "PwC", "Consensys Infura", "BairesDev"];

// Sourced from https://www.linkedin.com/in/fppalumbo/details/recommendations/
// Spanish-language recommendations translated to English for the marketing
// site. Originals preserved in source-of-truth profile. To add a photo,
// download from LinkedIn and save to /static/images/recs/<slug>.jpg, then
// set `photo: "/images/recs/<slug>.jpg"`.
const RECS = [
  {
    name: "Christian Alexis",
    role: "DevOps Engineer @ Endava",
    photo: "/images/recs/christian-alexis.png",
    linkedin: "https://www.linkedin.com/in/fppalumbo/details/recommendations/",
    text:
      "I worked with Fabio for three years at Endava across multiple cloud providers and DevOps projects. His leadership and depth of knowledge are exceptional — he drives innovative strategies for clients and delivers solutions at a very high bar. He led the technical and professional development of several teammates, myself included, present through every challenge. Technically brilliant and a great person — I hope we get to work together again.",
  },
  {
    name: "Jorge Luis Ferreiro",
    role: "DevOps Engineer · SysAdmin · SRE",
    photo: "/images/recs/jorge-luis-ferreiro.png",
    linkedin: "https://www.linkedin.com/in/fppalumbo/details/recommendations/",
    text:
      "I highly recommend Fabio for any organization looking to optimize its DevOps culture. His experience with Terraform, Azure, AWS, and CI/CD was instrumental in improving our deployment process, reducing downtime, and adopting best practices across every project. We worked back to back at Endava across discovery processes, leading the AM discipline, and he was a pillar of the LATAM DevOps community.",
  },
  {
    name: "Ernesto Szini",
    role: "IT Solution Architect · Endava (managed Fabio)",
    photo: "/images/recs/ernesto-szini.png",
    linkedin: "https://www.linkedin.com/in/fppalumbo/details/recommendations/",
    text:
      "I had the pleasure of working with Fabio on several projects migrating infrastructure to AWS/Azure using Terraform, CI/CD, and other toolchains. His knowledge, willingness to help, and experience were invaluable to the Endava DevOps community. Reliable and dedicated — I highly recommend him for any challenging project.",
  },
  {
    name: "Austin Thomé",
    role: "Lead Cloud Engineer · Agentic AI · Optum Financial",
    photo: "/images/recs/austin-thome.png",
    linkedin: "https://www.linkedin.com/in/fppalumbo/details/recommendations/",
    text:
      "I worked with Fabio on the same Networking team at Optum Financial. He's a great teammate — reliable, smart, helpful, and someone you can count on in a crisis. Fabio has my recommendation as a strong addition to any team that needs skilled tech personnel.",
  },
  {
    name: "Mykhailo Kosynskyi",
    role: "Lead Infrastructure & Security Consultant",
    photo: "/images/recs/mykhailo-kosynskyi.png",
    linkedin: "https://www.linkedin.com/in/fppalumbo/details/recommendations/",
    text:
      "Working with Fabio during the re-architecting of our application was a pleasure. He researched and integrated several third-party components that made the system more secure and maintainable from a user-lifecycle perspective. His initiative and creativity helped us build a stable system that now runs in production.",
  },
  {
    name: "Andrew Sue",
    role: "Manager · Digital Practices @ SSFM (was Fabio's manager at BlockFi)",
    photo: "/images/recs/andrew-sue.png",
    linkedin: "https://www.linkedin.com/in/fppalumbo/details/recommendations/",
    text:
      "I was Fabio's manager at BlockFi where he worked on our Infrastructure team. Fabio did his job competently and with a great attitude, and made himself indispensable to a couple of internal teams. He was great to work with — knowledgeable, collaborative, technical. I strongly recommend him.",
  },
  {
    name: "Brian Stevens",
    role: "Digital infrastructure builder · ex-BlockFi",
    photo: "/images/recs/brian-stevens.png",
    linkedin: "https://www.linkedin.com/in/fppalumbo/details/recommendations/",
    text:
      "Fabio and I worked together as Infrastructure Engineers at BlockFi during a period of incredible growth. He was a wonderful co-worker — personable, willing to help, and equally willing to ask for help. I would absolutely welcome the chance to work with him again.",
  },
  {
    name: "Fernando Martín Olivero",
    role: "Senior Full-Stack Engineer · Tech Lead",
    photo: "/images/recs/fernando-olivero.png",
    linkedin: "https://www.linkedin.com/in/fppalumbo/details/recommendations/",
    text:
      "Fabio is an excellent person and professional. Always finding the best way to solve a requirement and pushing for projects to land on time and at quality. Strong sense of responsibility.",
  },
  {
    name: "Claudio Romano",
    role: "IT Consultant · Banking & Fintech (was Fabio's manager)",
    photo: "/images/recs/claudio-romano.png",
    linkedin: "https://www.linkedin.com/in/fppalumbo/details/recommendations/",
    text:
      "A pleasure to work with Fabio. Deeply committed to deliverables and teamwork, continuously upskilling, very solid technically, a reference for the team — an all-terrain DevOps engineer.",
  },
  {
    name: "Fabián Leonardo Januszewski",
    role: "CTO @ Craftech",
    photo: "/images/recs/fabian-januszewski.png",
    linkedin: "https://www.linkedin.com/in/fppalumbo/details/recommendations/",
    text:
      "Fabio is an exceptional teammate. Committed, pragmatic, hard-working. You can always count on him whenever you need a hand. Thanks for everything.",
  },
  {
    name: "Dan Salazar",
    role: "DevOps & Engineering Manager (was Fabio's manager)",
    photo: "/images/recs/dan-salazar.png",
    linkedin: "https://www.linkedin.com/in/fppalumbo/details/recommendations/",
    text:
      "An excellent professional, with an unusual capacity to solve complex problems. His knowledge goes beyond a typical DevOps architect, adding value to every process he joins. A pleasure to have shared a year and a half of work.",
  },
  {
    name: "Martin Fernandez",
    role: "Co-Founder & CEO @ Crocante (was Fabio's manager)",
    photo: "/images/recs/martin-fernandez.png",
    linkedin: "https://www.linkedin.com/in/fppalumbo/details/recommendations/",
    text:
      "I worked with Fabio on several startup projects. His software development knowledge and team management carried us through multiple MVPs. His vision on automating tasks to improve lifecycle and deployment was liberating and empowering. Any CTO with him on the team will benefit from his collaboration and dedication.",
  },
  {
    name: "Abel Rebaudo",
    role: "Regional Manager @ Hotelbeds (former GM of 5CA)",
    photo: "/images/recs/abel-rebaudo.png",
    linkedin: "https://www.linkedin.com/in/fppalumbo/details/recommendations/",
    text:
      "As GM of 5CA I had the pleasure of working with Fabio for three years. He was essential to the company's technology development — contributing across client projects, Cloud, Monitoring, and Release Management. To me, Fabio is an indispensable piece of any engineering team — so much so that we later co-founded a venture together.",
  },
];

const SocialProof = () => {
  const trackRef = useRef(null);

  const scroll = (dir) => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.querySelector("article");
    const step = card ? card.getBoundingClientRect().width + 16 : 320;
    track.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  return (
    <Section>
      <Container>
        <SectionLabel>Recommendations</SectionLabel>

        <CarouselWrap>
          <ArrowBtn
            side="left"
            type="button"
            aria-label="Previous recommendation"
            onClick={() => scroll(-1)}
          >
            {"←"}
          </ArrowBtn>
          <Track ref={trackRef}>
            {RECS.map((r) => (
              <RecCard key={r.name}>
                <Head>
                  <Photo src={r.photo} aria-hidden="true" />
                  <Who>
                    <Name>{r.name}</Name>
                    <Role>{r.role}</Role>
                  </Who>
                </Head>
                <Body>{r.text}</Body>
                <ProfileLink
                  href={r.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View on LinkedIn
                </ProfileLink>
              </RecCard>
            ))}
          </Track>
          <ArrowBtn
            side="right"
            type="button"
            aria-label="Next recommendation"
            onClick={() => scroll(1)}
          >
            {"→"}
          </ArrowBtn>
        </CarouselWrap>
      </Container>
    </Section>
  );
};

export default SocialProof;
